/**
 * AS Colour account card (Settings, Integrations). An org connects its own
 * AS Colour login here, and picks how AS Colour ships to each location it runs.
 *
 * Hidden unless the backend's mode is on and the caller holds
 * `org_settings.manage` (the other three RBAC layers are the router, the handler
 * and, on a floor save, the floor owner check). With the mode off it makes one
 * environment-only read and renders nothing.
 *
 * The status block and credential editor follow `YunExpressSection`: write-only
 * fields, focus follows the editor. One difference: it has no sticky save bar. The
 * login is checked with AS Colour before it is stored, so Connect is its own
 * button and a refusal shows right under it.
 *
 * Every mutation has `retry: false` and the method list never refetches by itself:
 * either could log in to AS Colour again, and a wrong password may count against
 * the account. The browser only ever sees the masked login.
 *
 * Connect, Verify, shipping method, Deliver to, and the org's AS Colour prices: how
 * many, when they were last read, "Sync prices now" (it starts a read on the server
 * and returns at once; the status then polls until it ends) and the fill switch. The
 * ordering switch shows while connected: it allows Place order, and pressing Place
 * order is the confirm, so it has no dialog of its own. No price is ever sent to this card.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Unplug, X } from 'lucide-react';
import type { SupplierTradeDeliverTo } from '@threadheads/shared';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { noAutofillProps } from '@/components/ui/no-autofill';
import { SecretInput } from '@/components/ui/secret-input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { SectionError } from '@/components/shared/SectionError';
import { PrintSizeCodeCombobox } from '@/features/production-settings/components/PrintSizeCodeCombobox';
import { parseSupplierCatalogError } from '@/features/supplier-catalog/lib/supplier-catalog-errors';
import { useRole } from '@/hooks/use-role';
import { formatDateTime, useDateFnsLocale } from '@/lib/format-dates';
import { trpc, type RouterOutputs } from '@/lib/trpc';

type Floor = RouterOutputs['supplierTrade']['status']['floors'][number];
type Prices = RouterOutputs['supplierTrade']['status']['prices'];
type LoginFailure = 'refused' | 'unreadable' | 'unavailable' | 'too_many_attempts' | 'needs_login' | 'not_connected';

const LOGIN_FAILURE_KEY: Record<LoginFailure, string> = {
  refused: 'asColourAccount.failure.refused',
  unreadable: 'asColourAccount.failure.unreadable',
  unavailable: 'asColourAccount.failure.unavailable',
  too_many_attempts: 'asColourAccount.failure.tooManyAttempts',
  needs_login: 'asColourAccount.failure.needsLogin',
  not_connected: 'asColourAccount.failure.notConnected',
};

const PRICE_POLL_MS = 3_000;
/** The Send stamp's own gate: it uses no price read longer ago than this. */
const PRICE_FRESH_MS = 7 * 24 * 60 * 60_000;

const SHIPPING_METHOD_MAX = 100;
const isShippingMethod = (code: string): boolean => code.length > 0 && code.length <= SHIPPING_METHOD_MAX;

const FIELDS = [
  { key: 'name', label: 'asColourAccount.deliverTo.name', wide: true },
  { key: 'address1', label: 'asColourAccount.deliverTo.address1', wide: true },
  { key: 'address2', label: 'asColourAccount.deliverTo.address2', wide: true },
  { key: 'city', label: 'asColourAccount.deliverTo.city', wide: false },
  { key: 'state', label: 'asColourAccount.deliverTo.state', wide: false },
  { key: 'postcode', label: 'asColourAccount.deliverTo.postcode', wide: false },
  { key: 'country', label: 'asColourAccount.deliverTo.country', wide: false },
  { key: 'phone', label: 'asColourAccount.deliverTo.phone', wide: false },
  { key: 'email', label: 'asColourAccount.deliverTo.email', wide: false },
] as const;
type FieldKey = (typeof FIELDS)[number]['key'];
type Draft = Record<FieldKey, string>;

const draftOf = (deliverTo: SupplierTradeDeliverTo): Draft =>
  Object.fromEntries(FIELDS.map(({ key }) => [key, deliverTo[key] ?? ''])) as Draft;

const deliverToOf = (draft: Draft): SupplierTradeDeliverTo =>
  Object.fromEntries(FIELDS.map(({ key }) => [key, draft[key].trim() || null]));

const addressLines = (deliverTo: SupplierTradeDeliverTo): string[] =>
  [
    deliverTo.name,
    deliverTo.address1,
    deliverTo.address2,
    [deliverTo.city, deliverTo.state, deliverTo.postcode].filter(Boolean).join(' '),
    deliverTo.country,
    deliverTo.phone,
    deliverTo.email,
  ].filter((line): line is string => !!line);

function useFloorErrorText() {
  const { t } = useTranslation('settings');
  return (error: unknown): string => {
    const key = parseSupplierCatalogError(error)?.key;
    if (key === 'NOT_LOCATION_OWNER') return t('asColourAccount.errors.notOwner');
    if (key === 'TRADE_NOT_CONNECTED') return t('asColourAccount.errors.notConnected');
    return t('asColourAccount.errors.saveFailed');
  };
}

function DeliverToDialog({ floor, onClose }: { floor: Floor; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const floorErrorText = useFloorErrorText();
  const save = trpc.supplierTrade.saveFloor.useMutation({ retry: false });
  const [draft, setDraft] = useState<Draft>(() => draftOf(floor.deliverTo));

  const submit = () => {
    save.mutate(
      { locationId: floor.locationId, deliverTo: deliverToOf(draft) },
      {
        onSuccess: async () => {
          await utils.supplierTrade.status.invalidate();
          onClose();
        },
        onError: (error) => toast.error(floorErrorText(error)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('asColourAccount.deliverTo.dialogTitle', { location: floor.name })}</DialogTitle>
          <DialogDescription>{t('asColourAccount.deliverTo.dialogHelp')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map(({ key, label, wide }) => {
            const id = `ascolour-deliver-${floor.locationId}-${key}`;
            return (
              <div key={key} className={wide ? 'space-y-1.5 sm:col-span-2' : 'space-y-1.5'}>
                <Label htmlFor={id}>{t(label)}</Label>
                <Input
                  id={id}
                  value={draft[key]}
                  maxLength={200}
                  onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
                  className="h-10"
                  {...noAutofillProps}
                />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="h-10" onClick={onClose}>
            {t('production.common.cancel')}
          </Button>
          <Button type="button" className="h-10 gap-2" disabled={save.isPending} onClick={submit}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t('asColourAccount.deliverTo.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FloorRow({
  floor,
  methods,
  methodsLoading,
  methodsManual,
}: {
  floor: Floor;
  methods: Array<{ value: string; label: string }>;
  methodsLoading: boolean;
  methodsManual: boolean;
}) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const floorErrorText = useFloorErrorText();
  const save = trpc.supplierTrade.saveFloor.useMutation({ retry: false });
  const [editing, setEditing] = useState(false);

  const methodId = `ascolour-shipping-${floor.locationId}`;
  const hintId = `${methodId}-hint`;
  const lines = addressLines(floor.deliverTo);

  const chooseMethod = (code: string): boolean => {
    if (!isShippingMethod(code)) return false;
    save.mutate(
      { locationId: floor.locationId, shippingMethod: code },
      {
        onSuccess: () => void utils.supplierTrade.status.invalidate(),
        onError: (error) => toast.error(floorErrorText(error)),
      },
    );
    return true;
  };

  const chosenLabel = methods.find((method) => method.value === floor.shippingMethod)?.label ?? null;

  return (
    <li className="space-y-4 rounded-lg border p-4" data-testid={`ascolour-floor-${floor.locationId}`}>
      <p className="text-sm font-semibold">{floor.name}</p>

      <div className="space-y-1.5">
        <Label htmlFor={methodId}>{t('asColourAccount.shippingMethod')}</Label>
        <PrintSizeCodeCombobox
          id={methodId}
          ariaDescribedBy={hintId}
          ariaLabel={t('asColourAccount.shippingMethod')}
          value={floor.shippingMethod ? { code: floor.shippingMethod, label: chosenLabel } : null}
          options={methods.map((method) => ({ code: method.value, label: method.label }))}
          status={methods.length > 0 ? 'loaded' : 'empty'}
          preserveCase
          isValidCode={isShippingMethod}
          onSelect={chooseMethod}
          placeholder={t('asColourAccount.shippingMethodPlaceholder')}
          addButtonLabel={t('asColourAccount.useMethodButton')}
          createLabel={(code) => t('asColourAccount.useMethod', { code })}
          emptyLabel={methodsLoading ? t('asColourAccount.methodsLoading') : t('asColourAccount.noMethodMatches')}
          className="sm:w-full sm:max-w-md"
        />
        <p id={hintId} className="text-xs text-muted-foreground" role="status">
          {methodsLoading
            ? t('asColourAccount.methodsLoading')
            : methodsManual
              ? t('asColourAccount.methodsManual')
              : t('asColourAccount.methodsHelp')}
        </p>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{t('asColourAccount.deliverToLabel')}</p>
          {lines.length > 0 ? (
            <p className="whitespace-pre-line text-sm [overflow-wrap:anywhere]">{lines.join('\n')}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('asColourAccount.deliverToEmpty')}</p>
          )}
          {!floor.deliverToSaved && (
            <p className="mt-1 text-xs text-amber-800">{t('asColourAccount.deliverToPrefilled')}</p>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" className="h-10" onClick={() => setEditing(true)}>
          {t('asColourAccount.deliverToEdit')}
        </Button>
      </div>

      {editing && <DeliverToDialog floor={floor} onClose={() => setEditing(false)} />}
    </li>
  );
}

function PricesSection({ prices, connected }: { prices: Prices; connected: boolean }) {
  const { t, i18n } = useTranslation('settings');
  const locale = useDateFnsLocale();
  const utils = trpc.useUtils();
  const syncNow = trpc.supplierTrade.syncPricesNow.useMutation({ retry: false });
  const setFill = trpc.supplierTrade.setFillPoPrices.useMutation({ retry: false });

  const submitSync = () => {
    syncNow.mutate(undefined, {
      onSuccess: async (result) => {
        if (!result.started && result.reason === 'running') toast.info(t('asColourAccount.prices.busy'));
        if (!result.started && result.reason === 'needs_login') toast.error(t(LOGIN_FAILURE_KEY.needs_login));
        if (!result.started && result.reason === 'not_connected') toast.error(t(LOGIN_FAILURE_KEY.not_connected));
        await utils.supplierTrade.status.invalidate();
      },
      onError: () => toast.error(t('asColourAccount.prices.syncFailed')),
    });
  };

  const toggleFill = (enabled: boolean) => {
    setFill.mutate(
      { enabled },
      {
        onSuccess: () => void utils.supplierTrade.status.invalidate(),
        onError: () => toast.error(t('asColourAccount.errors.saveFailed')),
      },
    );
  };

  const tooOld = prices.fillPoPrices && prices.syncedAt !== null && Date.now() - Date.parse(prices.syncedAt) > PRICE_FRESH_MS;

  return (
    <div className="space-y-3" data-testid="ascolour-prices">
      <h4 className="text-sm font-semibold">{t('asColourAccount.prices.title')}</h4>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
        <dl className="min-w-0 flex-1 basis-48 space-y-1 text-sm">
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">{t('asColourAccount.prices.countLabel')}</dt>
            <dd className="font-medium tabular-nums">{prices.count.toLocaleString(i18n.language)}</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">{t('asColourAccount.prices.syncedLabel')}</dt>
            <dd className="font-medium">
              {prices.syncedAt ? formatDateTime(prices.syncedAt, locale) : t('asColourAccount.prices.neverSynced')}
            </dd>
          </div>
        </dl>
        {connected && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 gap-2"
            disabled={syncNow.isPending || prices.running}
            onClick={submitSync}
          >
            {syncNow.isPending || prices.running ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            )}
            {t('asColourAccount.prices.syncNow')}
          </Button>
        )}
        <div className="w-full" role="status">
          {prices.running ? (
            <p className="text-sm text-muted-foreground">{t('asColourAccount.prices.running')}</p>
          ) : prices.lastReadFailed ? (
            <p className="text-sm text-amber-800">{t('asColourAccount.prices.lastReadFailed')}</p>
          ) : null}
        </div>
      </div>
      <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
        <div className="min-w-0 flex-1">
          <Label htmlFor="ascolour-fill-po-prices" className="text-sm font-medium">
            {t('asColourAccount.prices.fillLabel')}
          </Label>
          <p id="ascolour-fill-po-prices-help" className="mt-1 text-xs text-muted-foreground">
            {t('asColourAccount.prices.fillHelp')}
          </p>
          {tooOld && <p className="mt-1 text-sm text-amber-800">{t('asColourAccount.prices.tooOld')}</p>}
        </div>
        <Switch
          id="ascolour-fill-po-prices"
          aria-describedby="ascolour-fill-po-prices-help"
          checked={prices.fillPoPrices}
          disabled={setFill.isPending}
          onCheckedChange={toggleFill}
        />
      </div>
    </div>
  );
}

/** Off here means the button can never show, so the switch says why. One already on can still be turned off. */
function OrderingSection({ enabled, allowed, turnedOff }: { enabled: boolean; allowed: boolean; turnedOff: boolean }) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const setOrdering = trpc.supplierTrade.setOrderingEnabled.useMutation({ retry: false });

  const toggle = (next: boolean) => {
    setOrdering.mutate(
      { enabled: next },
      {
        onSuccess: () => void utils.supplierTrade.status.invalidate(),
        onError: () => toast.error(t('asColourAccount.errors.saveFailed')),
      },
    );
  };

  return (
    <div className="space-y-3" data-testid="ascolour-ordering">
      <h4 className="text-sm font-semibold">{t('asColourAccount.ordering.title')}</h4>
      <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
        <div className="min-w-0 flex-1">
          <Label htmlFor="ascolour-ordering-enabled" className="text-sm font-medium">
            {t('asColourAccount.ordering.label')}
          </Label>
          <p id="ascolour-ordering-enabled-help" className="mt-1 text-xs text-muted-foreground">
            {t('asColourAccount.ordering.help')}
          </p>
          {!allowed && <p className="mt-1 text-sm text-amber-800">{t('asColourAccount.ordering.notAllowed')}</p>}
          {turnedOff && !enabled && (
            <p role="status" className="mt-1 text-sm text-amber-800">
              {t('asColourAccount.ordering.turnedOffByLogin')}
            </p>
          )}
        </div>
        <Switch
          id="ascolour-ordering-enabled"
          aria-describedby="ascolour-ordering-enabled-help"
          checked={enabled}
          disabled={setOrdering.isPending || (!allowed && !enabled)}
          onCheckedChange={toggle}
        />
      </div>
    </div>
  );
}

export function AsColourAccountCard({ hideWhenNotConnected = false }: { hideWhenNotConnected?: boolean }) {
  const { t } = useTranslation('settings');
  const { can } = useRole();
  const utils = trpc.useUtils();
  const canManage = can('org_settings.manage');

  const mode = trpc.supplierTrade.modeEnabled.useQuery(undefined, { enabled: canManage, staleTime: 5 * 60_000 });
  const enabled = canManage && mode.data?.ascolourTradeModeEnabled === true;
  // Polls only while a price read runs: this read goes to our database, never to AS Colour.
  const status = trpc.supplierTrade.status.useQuery(undefined, {
    enabled,
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.prices?.running ? PRICE_POLL_MS : false),
  });

  const connect = trpc.supplierTrade.connect.useMutation({ retry: false });
  const verify = trpc.supplierTrade.verify.useMutation({ retry: false });
  const disconnect = trpc.supplierTrade.disconnect.useMutation({ retry: false });

  const hasLogin = status.data?.hasCredentials === true && status.data.accountStatus !== null;
  const needsLogin = hasLogin && status.data?.accountStatus === 'needs_login';
  const connected = hasLogin && !needsLogin;

  // A read here can log in to AS Colour, so it runs once per visit, never on focus or reconnect.
  const methods = trpc.supplierTrade.shippingMethods.useQuery(undefined, {
    enabled: enabled && connected,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const [editing, setEditing] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<LoginFailure | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  /** A save with a different email turned ordering off in the SQL, and the reply does not say so. */
  const [orderingTurnedOff, setOrderingTurnedOff] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<'email' | 'toggle' | null>(null);

  useEffect(() => {
    const target = pendingFocusRef.current;
    if (!target) return;
    pendingFocusRef.current = null;
    (target === 'email' ? emailRef.current : toggleRef.current)?.focus();
  }, [editing]);

  // A refused login on the method read moved the account to needs_login: show it.
  const methodsManual = methods.data?.manual === true;
  useEffect(() => {
    if (methodsManual) void utils.supplierTrade.status.invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the answer flips, not when `utils` changes identity
  }, [methodsManual]);

  if (!enabled) return null;
  // Not while it is loading either: a pure client must not watch the card appear and vanish.
  if (hideWhenNotConnected && !hasLogin) return null;

  const openEditor = () => {
    pendingFocusRef.current = 'email';
    setFailure(null);
    setEditing(true);
  };

  const closeEditor = (focusToggle: boolean) => {
    if (focusToggle) pendingFocusRef.current = 'toggle';
    setLoginEmail('');
    setPassword('');
    setFailure(null);
    setEditing(false);
  };

  const submitConnect = () => {
    setFailure(null);
    const orderingWasOn = status.data?.orderingEnabled === true;
    connect.mutate(
      { loginEmail: loginEmail.trim(), password },
      {
        onSuccess: async (result) => {
          if (!result.ok) {
            setFailure(result.reason);
            // A wrong password is typed again; a server fault keeps what was typed.
            if (result.reason === 'refused') setPassword('');
            return;
          }
          utils.supplierTrade.shippingMethods.setData(undefined, {
            methods: result.methods ?? [],
            manual: result.methods === null,
          });
          // Close after the new status lands, so a good login does not flash amber.
          await utils.supplierTrade.status.invalidate();
          const nowOff = orderingWasOn && utils.supplierTrade.status.getData()?.orderingEnabled === false;
          setOrderingTurnedOff(nowOff);
          if (nowOff) toast.info(t('asColourAccount.ordering.turnedOffByLogin'));
          closeEditor(false);
        },
        onError: () => toast.error(t('asColourAccount.errors.saveFailed')),
      },
    );
  };

  const submitVerify = () => {
    verify.mutate(undefined, {
      onSuccess: async (result) => {
        if (result.ok) {
          toast.success(t('asColourAccount.verifyOk'));
          utils.supplierTrade.shippingMethods.setData(undefined, {
            methods: result.methods ?? [],
            manual: result.methods === null,
          });
          return;
        }
        // Connect's "Nothing was saved" is wrong here: this is the saved login, which is now stopped.
        toast.error(t(LOGIN_FAILURE_KEY[result.reason === 'refused' ? 'needs_login' : result.reason]));
        await utils.supplierTrade.status.invalidate();
      },
      onError: () => toast.error(t('asColourAccount.errors.verifyFailed')),
    });
  };

  const submitDisconnect = () => {
    disconnect.mutate(undefined, {
      onSuccess: async () => {
        setConfirmDisconnect(false);
        closeEditor(false);
        utils.supplierTrade.shippingMethods.reset();
        await utils.supplierTrade.status.invalidate();
      },
      onError: () => toast.error(t('asColourAccount.errors.disconnectFailed')),
    });
  };

  const floors = status.data?.floors ?? [];
  const canSubmit = loginEmail.trim().length > 0 && password.length > 0 && !connect.isPending;
  const failureId = 'ascolour-connect-failure';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          {t('asColourAccount.title')}
        </CardTitle>
        <CardDescription>{t('asColourAccount.description')}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {status.isPending ? (
          <div className="space-y-2" role="status" aria-label={t('asColourAccount.loading')}>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : status.isError ? (
          <SectionError
            variant="compact"
            title={t('asColourAccount.loadError')}
            onRetry={() => void status.refetch()}
            retryLabel={t('production.common.retry')}
            retrying={status.isFetching}
          />
        ) : (
          <>
            {connected && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-green-300 bg-green-50 p-3">
                <ShieldCheck className="h-5 w-5 shrink-0 text-green-700" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-green-900">{t('asColourAccount.connected')}</p>
                  <p className="text-sm text-green-900 [overflow-wrap:anywhere]">{status.data?.loginHint}</p>
                </div>
                {!editing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 gap-2 border-green-400 bg-white text-green-900 hover:bg-green-100"
                    disabled={verify.isPending}
                    onClick={submitVerify}
                  >
                    {verify.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    {t('asColourAccount.verify')}
                  </Button>
                )}
                {!editing && (
                  <Button
                    ref={toggleRef}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 gap-2 border-green-400 bg-white text-green-900 hover:bg-green-100"
                    onClick={openEditor}
                    aria-controls="ascolour-login-editor"
                    aria-expanded={false}
                  >
                    {t('asColourAccount.changeLogin')}
                  </Button>
                )}
              </div>
            )}

            {needsLogin && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3" role="alert">
                <ShieldAlert className="h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
                <div className="min-w-0 flex-1 basis-48">
                  <p className="text-sm font-medium text-amber-900">{t('asColourAccount.needsLogin')}</p>
                  <p className="text-sm text-amber-900">{t('asColourAccount.needsLoginHelp')}</p>
                </div>
                {!editing && (
                  <Button
                    ref={toggleRef}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 gap-2 border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
                    onClick={openEditor}
                    aria-controls="ascolour-login-editor"
                    aria-expanded={false}
                  >
                    {t('asColourAccount.updateLogin')}
                  </Button>
                )}
              </div>
            )}

            {!hasLogin && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <ShieldAlert className="h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-amber-900">{t('asColourAccount.notConnected')}</p>
                  <p className="text-sm text-amber-900">{t('asColourAccount.notConnectedHelp')}</p>
                </div>
                {!editing && (
                  <Button
                    ref={toggleRef}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 gap-2 border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
                    onClick={openEditor}
                    aria-controls="ascolour-login-editor"
                    aria-expanded={false}
                  >
                    {t('asColourAccount.connect')}
                  </Button>
                )}
              </div>
            )}

            {editing && (
              <form
                id="ascolour-login-editor"
                className="space-y-4 rounded-lg border bg-muted/20 p-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (canSubmit) submitConnect();
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="min-w-0 text-sm">{t('asColourAccount.editorHelp')}</p>
                  <Button type="button" variant="ghost" size="sm" className="h-10 gap-2" onClick={() => closeEditor(true)}>
                    <X className="h-4 w-4" aria-hidden="true" />
                    {t('production.common.cancel')}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ascolour-login-email">{t('asColourAccount.loginEmail')}</Label>
                  <Input
                    ref={emailRef}
                    id="ascolour-login-email"
                    name="ascolour-login-email-replacement"
                    value={loginEmail}
                    onChange={(event) => setLoginEmail(event.target.value)}
                    className="h-10 max-w-prose"
                    maxLength={320}
                    aria-describedby={failure ? failureId : undefined}
                    {...noAutofillProps}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ascolour-login-password">{t('asColourAccount.password')}</Label>
                  <SecretInput
                    id="ascolour-login-password"
                    name="ascolour-login-password-replacement"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    wrapperClassName="max-w-prose"
                    className="h-10"
                    maxLength={512}
                    aria-describedby={failure ? failureId : undefined}
                  />
                </div>
                {failure && (
                  <p id={failureId} role="alert" className="text-sm text-destructive">
                    {t(LOGIN_FAILURE_KEY[failure])}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button type="submit" className="h-10 gap-2" disabled={!canSubmit}>
                    {connect.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    {t('asColourAccount.verifyAndSave')}
                  </Button>
                </div>
              </form>
            )}

            {hasLogin && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold">{t('asColourAccount.floorsTitle')}</h4>
                {floors.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('asColourAccount.floorsEmpty')}</p>
                ) : (
                  <ul className="space-y-3">
                    {floors.map((floor) => (
                      <FloorRow
                        key={floor.locationId}
                        floor={floor}
                        methods={methods.data?.methods ?? []}
                        methodsLoading={connected && methods.isPending}
                        methodsManual={!connected || methods.isError || methods.data?.manual === true}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}

            {hasLogin && status.data?.prices && <PricesSection prices={status.data.prices} connected={connected} />}

            {connected && status.data && (
              <OrderingSection enabled={status.data.orderingEnabled} allowed={status.data.ordersAllowed} turnedOff={orderingTurnedOff} />
            )}

            {hasLogin && (
              <div className="flex justify-end border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirmDisconnect(true)}
                >
                  <Unplug className="h-4 w-4" aria-hidden="true" />
                  {t('asColourAccount.disconnect')}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('asColourAccount.disconnectTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('asColourAccount.disconnectBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('asColourAccount.disconnectKeep')}</AlertDialogCancel>
            <Button type="button" variant="destructive" className="gap-2" disabled={disconnect.isPending} onClick={submitDisconnect}>
              {disconnect.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('asColourAccount.disconnectConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
